<template>
  <div ref="elementRef" class="forward-request-tabs-component">
    <Swiper class="slider" :slides-per-view="getIsMobile ? 2 : 'auto'" :modules="[Navigation]" :navigation="true">
      <SwiperSlide>
        <div
          class="shipments-tab"
          :class="{ 'active-tab': isActiveTab(ForwardRequestTabsEnum.ReadyShipmentsTab) }"
          @click="handleTabClick(ForwardRequestTabsEnum.ReadyShipmentsTab)">
          <span class="text-sm">{{ t('site_pro.received_shipments') }}</span>
          <div class="tab-counter">{{ shipzeeProContent.readyItems.length }}</div>
        </div>
      </SwiperSlide>

      <SwiperSlide>
        <div
          class="shipments-tab"
          :class="{ 'active-tab': isActiveTab(ForwardRequestTabsEnum.InReviewShipmentsTab) }"
          @click="handleTabClick(ForwardRequestTabsEnum.InReviewShipmentsTab)">
          <span class="text-sm">{{ t('site_pro.in_review_shipments') }}</span>
          <div class="tab-counter">{{ shipzeeProContent.inReviewItems.length }}</div>
        </div>
      </SwiperSlide>

      <SwiperSlide>
        <div
          class="shipments-tab"
          :class="{ 'active-tab': isActiveTab(ForwardRequestTabsEnum.AllShipmentsTab) }"
          @click="handleTabClick(ForwardRequestTabsEnum.AllShipmentsTab)">
          <span class="text-sm">{{ t('site_pro.all_shipments') }}</span>
          <div class="tab-counter">{{ shipzeeProContent.allItems.length }}</div>
        </div>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'

import ForwardRequestTabsEnum from '~/enums/ForwardRequestTabsEnum'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import { useStoreConfig } from '~/store/storeConfig'

const { t } = useLang()
const shipzeeProContent = useSiteProContentStore()
const { getIsMobile } = useStoreConfig()
const elementRef = ref<HTMLElement | null>(null)

async function handleTabClick(tab: ForwardRequestTabsEnum, event?: Event) {
  // Prevent default behavior to avoid scrolling
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  // Store current scroll position before any potential scroll
  const scrollPosition = window.scrollY

  // Update the active tab
  shipzeeProContent.setActiveTab(tab)

  // Immediately restore scroll position to prevent any visible jump

  await nextTick(() => {
    window.scrollTo({
      top: scrollPosition,
      behavior: 'instant',
    })
  })
}

function isActiveTab(tab: ForwardRequestTabsEnum) {
  return shipzeeProContent.activeTab === tab
}
</script>

<style lang="postcss">
.forward-request-tabs-component {
  .shipments-tab {
    @apply flex items-center w-min whitespace-nowrap font-bold text-text2 dark:text-dmText2 cursor-pointer py-[0.8125rem] px-8 bg-neutral7 dark:bg-dmGray2 relative;

    .tab-counter {
      @apply flex items-center justify-around w-6 h-6 ml-1 bg-white dark:bg-dmGray1 rounded-full text-text1 dark:text-dmText1 text-xs font-medium;
    }

    &:hover {
      @apply bg-black text-white transition duration-200 ease-linear;
    }

    &.active-tab {
      @apply bg-brand-primary font-bold text-white dark:text-black;
    }
  }

  .slider {
    &.swiper-initialized {
      .swiper-button-prev::after {
        @apply text-brand-primary bg-white dark:bg-dmGray1 border-0 text-sm;
      }

      .swiper-button-next::after {
        @apply text-brand-primary bg-white dark:bg-dmGray1 border-0 text-sm;
      }
    }

    .swiper-wrapper {
      gap: 0.625rem;
    }
  }
}

.forward-request-tabs-component {
  .shipments-tab {
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
  }

  .slider {
    position: relative;

    &.swiper-initialized {
      overflow: hidden;

      .swiper-slide {
        width: min-content !important;
        flex-shrink: 1 !important;
      }

      .swiper-button-disabled {
        display: none !important;
        opacity: 1;
      }

      .swiper-button-prev::after {
        content: '\f060' !important;
      }

      .swiper-button-next::after {
        content: '\f061' !important;
      }
    }
  }
}
</style>

<style lang="postcss">
.wl {
  .forward-request-tabs-component {
    .shipments-tab {
      @apply p-2 bg-white rounded-md border border-neutral5;

      .tab-counter {
        @apply rounded-sm font-semibold border border-neutral5 ml-2;
      }

      &:hover {
        @apply text-black bg-neutral6;
      }

      &.active-tab {
        @apply rounded-t-md border-b-0 rounded-b-none text-black bg-neutral7 font-bold border-neutral7 pb-4;

        .tab-counter {
          @apply border-neutral7;
        }
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      bottom: 6px;

      &::after {
        @apply !border rounded-sm;
      }
    }
  }
}
</style>
